import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { setAccessToken } from '@/lib/api/client';
import { getMeApi } from '@/lib/auth/authApi';
import { safeInternalPath } from '@/lib/safe-redirect';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Point d'atterrissage après le login sur l'admin avec fromSource=site.
 * Lit le token depuis le fragment d'URL (#token=...&redirectTo=...),
 * récupère le profil utilisateur, hydrate le store, puis redirige.
 *
 * Le fragment (#) n'est jamais envoyé au serveur (sécurité navigateur).
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();

  useEffect(() => {
    // Error from API redirect (query param) — e.g. registration closed
    const queryError = searchParams.get('error');
    if (queryError) {
      const adminUrl =
        (import.meta.env.VITE_URL_ADMIN as string | undefined)?.replace(/\/$/, '') || 'http://localhost:5231';
      window.location.replace(`${adminUrl}/login?error=${encodeURIComponent(queryError)}`);
      return;
    }

    const hash = window.location.hash.slice(1); // enlève le #
    const params = new URLSearchParams(hash);
    if (params.get('mfa') === '1') {
      window.history.replaceState(null, '', window.location.pathname);
      const adminUrl =
        (import.meta.env.VITE_URL_ADMIN as string | undefined)?.replace(/\/$/, '') || 'http://localhost:5231';
      const query = new URLSearchParams({
        fromSource: 'site',
        redirectTo: safeInternalPath(searchParams.get('redirectTo')),
      });
      window.location.replace(`${adminUrl}/auth/callback?${query}#${params}`);
      return;
    }
    const token = params.get('token');
    const redirectTo = safeInternalPath(params.get('redirectTo'));

    if (!token) {
      navigate('/', { replace: true });
      return;
    }

    // Nettoyer le fragment de l'URL immédiatement (sécurité : ne pas laisser le token dans l'historique)
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    setAccessToken(token);

    // Si redirectTo est une page générique/auth, aller vers le landing par rôle
    const isGeneric =
      redirectTo === '/' || redirectTo.startsWith(ROUTES.LOGIN) || redirectTo.startsWith(ROUTES.REGISTER);
    const destination = isGeneric ? ROUTES.ROLE_LANDING : redirectTo;

    getMeApi()
      .then(user => {
        login(user, token);
        navigate(destination, { replace: true });
      })
      .catch(() => {
        navigate('/', { replace: true });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className='min-h-screen flex items-center justify-center'>
      <p className='text-slate-500 text-sm'>Connexion en cours…</p>
    </div>
  );
}
